/* eslint-disable no-unused-vars */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import SectionTitle from "../UI/SectionTitle";
import { MAIN, st } from "../../constants/styles";
import {
  getAddressFields,
  getPasswordFields,
  getPersonalFields,
} from "../../constants/profileData";
import FormInput from "../UI/FormInput";
import EmptyState from "../UI/EmptyState";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { updateCurrentUserApi } from "../../services/auth";

// import { useMutation, useQueryClient } from "@tanstack/react-query";
// import { useRef, useState } from "react";
// import SectionTitle from "../UI/SectionTitle";
// import { MAIN, st } from "../../constants/styles";

const PERSONAL_EDITABLE = ["phoneNumber", "dateOfBirth"];

const SectionAccountManagement = ({ user, loading, isError }) => {
  const [activeTab, setActiveTab] = useState("personal");
  const TABS = [
    ["personal", "Personal Details"],
    ["password", "Change Password"],
  ];
  // const { user: USER, isLoading, isError } = useCurrentUser();
  const queryClient = useQueryClient();
  // const { toast } = useToast();

  const personalRefs = {
    phoneNumber: useRef(null),
    dateOfBirth: useRef(null),
  };

  const addressRefs = {
    addressLine1: useRef(null),
    addressLine2: useRef(null),
    landmark: useRef(null),
    city: useRef(null),
    county: useRef(null),
    postalCode: useRef(null),
    country: useRef(null),
  };

  const passwordRefs = {
    currentPassword: useRef(null),
    newPassword: useRef(null),
    confirmPassword: useRef(null),
  };

  const updateMutation = useMutation({
    mutationFn: updateCurrentUserApi,
    onSuccess: (data) => {
      if (data?.isSuccess !== false) {
        toast.success("Your details have been saved successfully.");
      } else {
        toast.infos(data?.message || "Unable to save changes.");
      }
    },
    onError: (error) => {
      toast.error(`Update Failed ${error}`);
    },
  });

  const handleSave = () => {
    console.log("Saving with values:", {
      phoneNumber: personalRefs.phoneNumber.current?.value,
      dateOfBirth: personalRefs.dateOfBirth.current?.value,
      address: {
        addressLine1: addressRefs.addressLine1.current?.value,
        addressLine2: addressRefs.addressLine2.current?.value,
        landmark: addressRefs.landmark.current?.value,
        city: addressRefs.city.current?.value,
        county: addressRefs.county.current?.value,
        postalCode: addressRefs.postalCode.current?.value,
        country: addressRefs.country.current?.value,
      },
    });

    updateMutation.mutate({
      user: {
        phoneNumber: personalRefs.phoneNumber.current?.value,
        dateOfBirth: personalRefs.dateOfBirth.current?.value || null,
        address: {
          addressLine1: addressRefs.addressLine1.current?.value || null,
          addressLine2: addressRefs.addressLine2.current?.value || null,
          landmark: addressRefs.landmark.current?.value || null,
          city: addressRefs.city.current?.value || null,
          county: addressRefs.county.current?.value || null,
          postalCode: addressRefs.postalCode.current?.value || null,
          country: addressRefs.country.current?.value || null,
        },
      },
    });
  };

  const handlePasswordSave = () => {
    const currentPassword = passwordRefs.currentPassword.current?.value;
    const newPassword = passwordRefs.newPassword.current?.value;
    const confirmPassword = passwordRefs.confirmPassword.current?.value;

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("All password fields are required.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    updateMutation.mutate({
      user: { currentPassword, newPassword },
    });
  };

  if (loading) return <p style={{ padding: 16 }}>Loading...</p>;
  if (isError || !user) {
    return (
      <EmptyState
        emoji="⚠️"
        message="Please login. Your session may have expired."
        action={
          <Link
            to="/auth/login"
            style={{
              ...st.btnGreen,
              textDecoration: "none",
              display: "inline-block",
              marginTop: 12,
              padding: "10px 24px",
            }}
          >
            Login
          </Link>
        }
      />
    );
  }

  return (
    <div>
      <SectionTitle
        title="Account Management"
        subtitle="Update your personal details and security settings"
      />

      <div className="d-flex gap-3 mb-28 border-bottom pb-2">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              background: "none",
              border: "none",
              borderBottom:
                activeTab === key
                  ? `2.5px solid ${MAIN}`
                  : "2.5px solid transparent",
              color: activeTab === key ? MAIN : "#666",
              fontWeight: activeTab === key ? 700 : 500,
              fontSize: 14,
              padding: "6px 4px 10px",
              cursor: "pointer",
              marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === "personal" && (
        <div>
          <div className="row g-3" style={{ paddingRight: 12 }}>
            {getPersonalFields(user).map((f) => (
              <FormInput
                key={f.label}
                label={f.label}
                type={f.type}
                defaultValue={f.value}
                options={f.options}
                readOnly={f.readOnly}
                inputRef={
                  PERSONAL_EDITABLE.includes(f.key)
                    ? personalRefs[f.key]
                    : undefined
                }
              />
            ))}
          </div>

          <div
            style={{
              margin: "24px 0 12px",
              fontWeight: 600,
              fontSize: 14,
              color: "#333",
              paddingRight: 12,
            }}
          >
            Address
          </div>

          <div className="row g-3" style={{ paddingRight: 12 }}>
            {getAddressFields(user).map((f) => (
              <FormInput
                key={f.label}
                label={f.label}
                defaultValue={f.value}
                inputRef={addressRefs[f.key]}
              />
            ))}
          </div>
          <hr />
          <div className="col-12 mt-3 mb-2" style={{ paddingRight: 12 }}>
            <button
              style={st.btnGreen}
              onClick={handleSave}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      )}

      {activeTab === "password" && (
        <div className="row g-3" style={{ paddingRight: 12 }}>
          {getPasswordFields().map((f) => (
            <FormInput
              key={f.key}
              label={f.label}
              type={f.type}
              inputRef={passwordRefs[f.key]}
            />
          ))}
          <hr />
          <div className="col-12 mt-3 mb-2">
            <button
              style={st.btnGreen}
              onClick={handlePasswordSave}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? "Saving..." : "Update Password"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionAccountManagement;
